import React, { useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import {
  AddComment as AddCommentIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { ManagerCommentDialog } from '~/components/CommentCell/ManagerCommentDialog.tsx';
import {
  COMMENT_EMPTY_PLACEHOLDER,
  COMMENT_PREVIEW_LIMIT,
  MANAGER_COMMENT_LABELS,
} from '~/components/CommentCell/constants.ts';
import { commentCellStyles } from '~/components/CommentCell/styles.ts';
import { getCommentPlainText } from '~/components/CommentCell/utils.ts';

interface IManagerCommentCellProps {
  initialComment?: string;
  readOnly?: boolean;
  open?: boolean;
  labels?: Partial<Record<keyof typeof MANAGER_COMMENT_LABELS, string>>;
  onSave?: (newComment: string) => Promise<void> | void;
  onOpenChange?: (open: boolean) => void;
}

export const ManagerCommentCell: React.FC<IManagerCommentCellProps> = ({
  initialComment,
  readOnly = false,
  open: controlledOpen,
  labels,
  onSave,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (nextOpen: boolean) => {
    onOpenChange?.(nextOpen);

    if (controlledOpen === undefined) {
      setUncontrolledOpen(nextOpen);
    }
  };
  const commentText = getCommentPlainText(initialComment);
  const hasComment = !!commentText.trim();

  if (!hasComment && readOnly) {
    return (
      <Typography variant="caption" color="text.disabled">
        {COMMENT_EMPTY_PLACEHOLDER}
      </Typography>
    );
  }

  return (
    <>
      <Box
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        sx={readOnly ? commentCellStyles.managerReadOnlyBox : commentCellStyles.managerBox}
      >
        {hasComment ? (
          <>
            <Typography variant="body2" color="text.primary" sx={commentCellStyles.managerText}>
              {commentText}
            </Typography>
            {commentText.length > COMMENT_PREVIEW_LIMIT && (
              <InfoIcon sx={commentCellStyles.infoIcon} />
            )}
          </>
        ) : (
          <Typography variant="body2" sx={commentCellStyles.empty}>
            <AddCommentIcon sx={commentCellStyles.addIcon} /> {MANAGER_COMMENT_LABELS.add}
          </Typography>
        )}

        <Tooltip
          title={
            readOnly ? MANAGER_COMMENT_LABELS.viewTooltip : MANAGER_COMMENT_LABELS.editTooltip
          }
        >
          {readOnly ? (
            <VisibilityIcon className="action-icon" sx={commentCellStyles.viewIcon} />
          ) : (
            <EditIcon className="action-icon" sx={commentCellStyles.editIcon} />
          )}
        </Tooltip>
      </Box>

      <ManagerCommentDialog
        initialComment={initialComment}
        open={open}
        readOnly={readOnly}
        isSubmitting={isSubmitting}
        onSave={onSave}
        onClose={() => setOpen(false)}
        setIsSubmitting={setIsSubmitting}
        labels={labels}
      />
    </>
  );
};
